import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

interface Task {
  title: string;
  category: 'Technique' | 'Repertoire';
}

interface QuizQuestion {
  question: string;
  answer: string;
}

interface Lesson {
  id: string;
  lessonId: string;
  studentId: string;
  teacherId?: string;
  createdAt: any;
  summaryTxt?: string; // Legacy field
  overview?: string;
  practiceReminders?: string | Array<{
    type: 'section' | 'subsection' | 'bullet' | 'text';
    content: string;
    level: number;
  }>; // Support both old string and new structured array format
  mindsetMessages?: string;
  keyQuotes?: string[];
  tasks: Task[];
  quiz: QuizQuestion[];
  transcript?: string;
  status: string;
  processedAt: any;
}

interface Student {
  uid: string;
  name: string;
  nickname: string;
  instrument: string;
}

const TeacherLessonSummaries: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { studentId } = useParams<{ studentId: string }>();
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [student, setStudent] = useState<Student | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedLessons, setExpandedLessons] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');

  useEffect(() => {
    if (!currentUser || currentUser.role !== 'teacher') {
      navigate('/teacher/dashboard');
      return;
    }

    if (!studentId) {
      setError('Student ID is required');
      setLoading(false);
      return;
    }

    // Check if student is linked to this teacher
    if (!currentUser.linkedStudents?.includes(studentId)) {
      setError('You do not have access to this student');
      setLoading(false);
      return;
    }

    fetchStudentAndLessons();
  }, [currentUser, studentId, navigate]);

  const fetchStudentAndLessons = async () => {
    try {
      setLoading(true);
      
      // Fetch student info
      const studentDoc = await getDocs(query(collection(db, 'users'), where('__name__', '==', studentId)));
      if (!studentDoc.empty) {
        const studentData = studentDoc.docs[0].data();
        setStudent({
          uid: studentId,
          ...studentData
        } as Student);
      }

      // Fetch lessons for this student
      let lessonsQuery = query(
        collection(db, 'lessons'),
        where('studentId', '==', studentId),
        orderBy('createdAt', 'desc')
      );

      try {
        const querySnapshot = await getDocs(lessonsQuery);
        const lessonsData: Lesson[] = [];

        querySnapshot.forEach((doc) => {
          lessonsData.push({
            id: doc.id,
            ...doc.data()
          } as Lesson);
        });

        setLessons(lessonsData);
      } catch (indexError: any) {
        // If index not ready, fallback to simple query and sort client-side
        if (indexError.code === 'failed-precondition') {
          console.log('Index not ready, using fallback query...');
          
          const fallbackQuery = query(
            collection(db, 'lessons'),
            where('studentId', '==', studentId)
          );

          const querySnapshot = await getDocs(fallbackQuery);
          const lessonsData: Lesson[] = [];

          querySnapshot.forEach((doc) => {
            lessonsData.push({
              id: doc.id,
              ...doc.data()
            } as Lesson);
          });

          // Sort by createdAt descending on client side
          lessonsData.sort((a, b) => {
            const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
            const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
            return bTime - aTime;
          });

          setLessons(lessonsData);
        } else {
          throw indexError;
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load student data');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (lessonId: string) => {
    const newExpanded = new Set(expandedLessons);
    if (newExpanded.has(lessonId)) {
      newExpanded.delete(lessonId);
    } else {
      newExpanded.add(lessonId);
    }
    setExpandedLessons(newExpanded);
  };

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return 'Unknown date';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPreviewText = (text: string, maxLength: number = 80): string => {
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mb-4"></div>
          <p className="text-gray-600">Loading lesson summaries...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/teacher/dashboard')}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            Back to Teacher Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <nav className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 shadow-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="flex items-center">
                <div className="text-3xl mr-3">📚</div>
                <h1 className="text-xl font-bold text-white">
                  Teacher Dashboard
                </h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-white/90 font-medium">
                {student ? `${student.name}'s Lessons` : 'Student Lessons'}
              </span>
              <button
                onClick={() => navigate('/teacher/dashboard')}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 backdrop-blur-sm"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {student ? `${student.name}'s Lesson Summaries` : 'Student Lesson Summaries'}
            </h2>
            {student && (
              <p className="text-gray-600">
                {student.instrument} • {lessons?.length || 0} lesson{(lessons?.length || 0) !== 1 ? 's' : ''} recorded
              </p>
            )}
          </div>

          {!lessons || lessons.length === 0 ? (
            <div className="bg-white shadow rounded-lg p-8 text-center">
              <div className="text-gray-400 mb-4">
                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No lessons yet</h3>
              <p className="text-gray-600 mb-4">
                This student hasn't recorded any lessons yet.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {lessons && lessons.map((lesson) => (
                <div key={lesson.id} className="bg-white/80 backdrop-blur-sm shadow-xl rounded-xl overflow-hidden border border-white/20 hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
                  <div className="px-8 py-6">
                    <div className="flex justify-between items-start">
                      <div 
                        className="flex-1 cursor-pointer hover:bg-gradient-to-r hover:from-indigo-50/50 hover:to-purple-50/50 transition-all duration-200 rounded-lg p-3 -m-3"
                        onClick={() => toggleExpanded(lesson.id)}
                      >
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          {formatDate(lesson.createdAt)}
                        </h3>
                        <p className="text-gray-600 text-base">
                          {getPreviewText(lesson.overview || lesson.summaryTxt || 'No summary available')}
                        </p>
                      </div>
                      <div className="ml-4 flex-shrink-0 flex items-center space-x-2">
                        <button
                          onClick={() => toggleExpanded(lesson.id)}
                          className="text-indigo-500 hover:text-indigo-700 p-2 rounded-lg hover:bg-indigo-50 transition-all duration-200 shadow-sm hover:shadow-md"
                        >
                          <svg
                            className={`h-5 w-5 transform transition-transform ${
                              expandedLessons.has(lesson.id) ? 'rotate-180' : ''
                            }`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>

                  {expandedLessons.has(lesson.id) && (
                    <div className="px-8 py-6 border-t border-gray-200/50 bg-gradient-to-br from-gray-50/80 to-indigo-50/30">
                      <div className="space-y-8">
                        {/* Lesson Overview */}
                        <div className="bg-white/60 backdrop-blur-sm rounded-xl p-6 border border-white/40">
                          <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                            <div className="w-6 h-6 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full mr-3 flex items-center justify-center">
                              <span className="text-white text-sm">📝</span>
                            </div>
                            Lesson Overview
                          </h4>
                          <p className="text-gray-700 leading-relaxed text-base">{lesson.overview || lesson.summaryTxt || 'No overview available'}</p>
                        </div>

                        {/* Practice Reminders */}
                        {lesson.practiceReminders && (
                          <div className="bg-white/60 backdrop-blur-sm rounded-xl p-6 border border-white/40">
                            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                              <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full mr-3 flex items-center justify-center">
                                <span className="text-white text-sm">💡</span>
                              </div>
                              Practice Reminders
                            </h4>
                            {Array.isArray(lesson.practiceReminders) ? (
                              <div className="space-y-3">
                                {lesson.practiceReminders.map((item, index) => {
                                  if (item.type === 'section') {
                                    return (
                                      <div key={index} className="mt-4 mb-2">
                                        <h5 className="text-sm font-semibold text-indigo-700 bg-indigo-50 px-3 py-2 rounded-md">
                                          {item.content}
                                        </h5>
                                      </div>
                                    );
                                  } else if (item.type === 'subsection') {
                                    return (
                                      <div key={index} className="ml-4">
                                        <h6 className="text-sm font-medium text-indigo-600">
                                          {item.content}
                                        </h6>
                                      </div>
                                    );
                                  } else if (item.type === 'bullet') {
                                    return (
                                      <div key={index} className="flex items-start ml-8">
                                        <span className="text-indigo-600 mr-2">•</span>
                                        <span className="text-gray-700 flex-1">
                                          {item.content}
                                        </span>
                                      </div>
                                    );
                                  } else {
                                    return (
                                      <div key={index} className="text-gray-700">
                                        {item.content}
                                      </div>
                                    );
                                  }
                                })}
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {typeof lesson.practiceReminders === 'string' ? lesson.practiceReminders.split('\n').map((line, index) => {
                                  const trimmedLine = line.trim();
                                  if (!trimmedLine || !trimmedLine.startsWith('-')) return null;
                                  
                                  const reminderText = trimmedLine.substring(1).trim();
                                  return (
                                    <div key={index} className="flex items-start">
                                      <span className="text-indigo-600 mr-2">•</span>
                                      <span className="text-gray-700 flex-1">
                                        {reminderText}
                                      </span>
                                    </div>
                                  );
                                }) : (
                                  <p className="text-gray-500 italic">No practice reminders available</p>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Key Mindset Messages */}
                        {lesson.mindsetMessages && (
                          <div className="bg-white/60 backdrop-blur-sm rounded-xl p-6 border border-white/40">
                            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                              <div className="w-6 h-6 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full mr-3 flex items-center justify-center">
                                <span className="text-white text-sm">🧠</span>
                              </div>
                              Key Mindset Messages
                            </h4>
                            <div className="text-gray-700 whitespace-pre-wrap">
                              {lesson.mindsetMessages}
                            </div>
                          </div>
                        )}

                        {/* Key Quotes */}
                        {lesson.keyQuotes && lesson.keyQuotes.length > 0 && (
                          <div className="bg-white/60 backdrop-blur-sm rounded-xl p-6 border border-white/40">
                            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                              <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mr-3 flex items-center justify-center">
                                <span className="text-white text-sm">💬</span>
                              </div>
                              Key Quotes
                            </h4>
                            <div className="space-y-3">
                              {lesson.keyQuotes.map((quote, index) => (
                                <blockquote key={index} className="border-l-4 border-indigo-400 pl-4 italic text-gray-700">
                                  "{quote}"
                                </blockquote>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default TeacherLessonSummaries;

